import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  IcoEllipse,
  IcoPrivateStudy,
  IcoPublicStudy,
  IcoSearch,
  IcoStatusComplete,
  IcoStatusIncomplete,
  IcoStatusInProgress,
  IEvent,
  Input,
  IPagination,
  Pagination,
} from '../../../component';
import { setUserPermissions } from '../../../layout/redux/layoutActions';
import nls from './nls/pt-BR.json';
import './studiesList.scss';
import Dropdown from './components/dropdown';
import { Dispatch } from 'redux';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import {
  closeAllDropdowns,
  setIsDropdownOpen,
  setSelectedStudy,
  setTitleSearchInput,
  studiesSelector,
} from './redux/studies.reducer';
import { Study } from './redux/studies.model';
import { EditStudyModal } from './components/editStudyModal';
import { ConfirmDeleteModal } from './components/confirmDeleteModal';
import { modalOpen, setIsActive } from '../redux/dashboardActions';
import { handleClickExitStudyCreationGuide } from './components/studyCreationGuide/components';
import { useStudiesList } from './useStudiesList';

const initialPage = {
  currentPage: 1,
  totalPages: 1,
  totalResults: 1,
};

const StudiesList: React.FC = () => {
  const dispatch: Dispatch<any> = useDispatch();
  const [heightHeader, setHeightHeader] = useState<number | undefined>(0);
  const { titleSearchInput, initialStep, currentStep, pagination } =
    useTypedSelector(studiesSelector);
  const [pages, setPages] = useState<IPagination>(initialPage);
  const { statusModal, displayStudyCreationGuide } = useTypedSelector(
    (state) => state.dashboardState
  );
  const {
    isActive: { menuAction },
  } = useSelector((state: any) => state.dashboardState);
  useEffect(() => {
    const boxHeader = document.getElementById('box-header');

    setHeightHeader(boxHeader?.offsetHeight);
    dispatch(setUserPermissions());
    if (displayStudyCreationGuide)
      dispatch(handleClickExitStudyCreationGuide(currentStep, initialStep));
  }, []);
  const { getList } = useStudiesList();
  useEffect(() => {
    getList(pages.currentPage.toString(), titleSearchInput, setPages);
  }, [pages.currentPage]);
  useEffect(() => {
    setPages(pagination);
  }, [pagination]);

  return (
    <>
      <div className={`studies`}>
        <div className="wrapper">
          <Header />
          <div className="content">
            <SearchBar />
            <Table />

            <Pagination
              paginationObj={pages}
              setPagination={setPages}
              wrapperStyle={{ paddingRight: 24 }}
            />
          </div>
        </div>
      </div>
      {statusModal === 'edit study modal' && <EditStudyModal />}
      {statusModal === 'confirm delete study modal' && <ConfirmDeleteModal />}
    </>
  );
};

const Header: React.FC = () => {
  const dispatch = useDispatch();
  const handleClickNewReport = () => {
    dispatch([
      modalOpen('new study modal'),
      setIsActive({ name: 'menuAction', value: '' }),
    ]);
  };
  return (
    <div className="header">
      <h3 className="studies-title">{nls.title}</h3>
      <Button
        className="btn primary normal block"
        cy="NewReportStudy"
        style={{ width: 151, height: 42 }}
        onClick={handleClickNewReport}
      >
        {nls.newReport}
      </Button>
    </div>
  );
};

const SearchBar: React.FC = () => {
  const { pagination, titleSearchInput } = useTypedSelector(studiesSelector);
  const { getList } = useStudiesList();
  const dispatch = useDispatch();
  const handleInput = (e: IEvent) => {
    dispatch(setTitleSearchInput(e.value));
  };
  const handleClickSearch = async () => {
    await getList('1', titleSearchInput);
  };
  const handleKey = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      await handleClickSearch();
    }
  };
  return (
    <div className="search-bar">
      <div
        className="input-wrapper"
        style={{ border: '1px solid #A2A4A4', flexGrow: 1, borderRadius: 4 }}
      >
        <Input
          left={
            <Button
              cy="SearchBarStudiesList"
              type="link"
              onClick={handleClickSearch}
            >
              <IcoSearch
                cy="SearchBarStudiesList"
                style={{ color: '#A2A4A4', width: 16, height: 16 }}
              />
            </Button>
          }
          action={handleInput}
          cy="SearchStudy"
          value={titleSearchInput}
          placeholder={nls.searchFor}
          style={{
            backgroundColor: '#FFF',
            color: '#000',
          }}
          onKeyDown={handleKey}
        />
      </div>
      <div>
        <span style={{ fontWeight: 700 }}>{pagination.totalResults} </span>
        <span>{nls.results}</span>
      </div>
    </div>
  );
};

const Table: React.FC = () => {
  const statusIconMapper = {
    in_progress: <IcoStatusInProgress cy="StudiesTables" />,
    incomplete: <IcoStatusIncomplete cy="StudiesTable" />,
    finished: <IcoStatusComplete cy="StudiesTable" />,
  };
  const dispatch = useDispatch();

  const { isDropdownOpen, studiesList } = useTypedSelector(studiesSelector);
  const handleClickDropdownButton = (study: Study, index: number) => {
    dispatch(closeAllDropdowns());
    dispatch([
      setIsDropdownOpen({ [index]: !isDropdownOpen[index] }),
      setSelectedStudy(isDropdownOpen[index] ? null : study),
    ]);
  };
  return (
    <table className="table" style={{ fontFamily: 'Open Sans, sans-serif' }}>
      <thead>
        <tr>
          <td>{nls.table.header.name}</td>
          <td>{nls.table.header.author}</td>
          <td>{nls.table.header.created_at}</td>
          <td>{nls.table.header.status}</td>
          <td></td>
        </tr>
      </thead>
      <tbody>
        {studiesList?.map((data, index) => (
          <tr key={index}>
            <td>
              <span>
                {data.is_public ? (
                  <IcoPublicStudy cy={`StudiesList[${index}]`} />
                ) : (
                  <IcoPrivateStudy cy={`StudiesList[${index}]`} />
                )}
              </span>
              <span>{data.name}</span>
            </td>
            <td>{data.author}</td>
            <td>{data.created_at}</td>
            <td>{statusIconMapper[data.status]}</td>
            <td>
              <Button
                className="link"
                cy={`StudiesTable[${index}]`}
                onClick={() => handleClickDropdownButton(data, index)}
              >
                <IcoEllipse cy={`StudiesTableDot1[${index}]`} />
                <IcoEllipse cy={`StudiesTableDot2[${index}]`} />
                <IcoEllipse cy={`StudiesTableDot3[${index}]`} />
              </Button>
              {isDropdownOpen[index] && <Dropdown />}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default StudiesList;
